/**
 * Data Explorer view config types.
 *
 * A view config IS an `ExceptionConfig` (so it slots straight into
 * `DataViewTable`, `DataViewFilterPanel`, `useExceptionUrlState`
 * and `useExceptionFilterValues` with zero shared-type changes) plus
 * the browse-only concerns: page header copy, the fetchers, FP-derived
 * filter options and the server-sort contract.
 */
import type { ExceptionConfig, ExceptionFilterDef } from '../exceptions/types'
import type {
  DataExplorerListResponse,
  DataExplorerProductFacets,
} from '../../services/inspector/api'

export type DataViewKey = 'sales' | 'products' | 'clients'

/**
 * FP-derived option sources resolved once per FP by the route:
 * `outlets` rides the list response (sales/products), `main_categories`
 * comes from the products facets endpoint. Filters can't derive these
 * from rows - the page only sees the current page.
 */
export interface DataViewFacets {
  outlets?: Array<{ id: string; name: string }>
  main_categories?: Array<{ value: string | null; count: number }>
}

export interface DataExplorerViewConfig extends ExceptionConfig {
  viewKey: DataViewKey
  pageHeader: { title: string; description: string; breadcrumb: string }
  /** `userCanAccessInspectorItem(role, 'data', id)` id - keeps today's gating. */
  fpUserAccessId: string
  fetchPage: (
    fpCode: string,
    params: Record<string, any>,
  ) => Promise<DataExplorerListResponse>
  fetchFacets?: (fpCode: string) => Promise<DataViewFacets>
  /** Fill FP-dependent filter options; absent = `filters` used as-is. */
  resolveFilters?: (facets: DataViewFacets | null) => ExceptionFilterDef[]
  serverSort: {
    /** Column keys (table-side) the server can sort. */
    sortableKeys: string[]
    defaultSortBy: string
    defaultSortDir: 'asc' | 'desc'
  }
}

/**
 * Column-key -> backend `sort_by` translation. Column keys stay
 * table-shaped (`errors` is the Issues column); the API speaks
 * aggregate names.
 */
export const SORT_KEY_MAP: Record<string, string> = {
  errors: 'open_issues',
  issues_last_detected_at: 'last_detected',
  price_excluding_tax: 'price',
}

export const toApiSortKey = (columnKey: string): string =>
  SORT_KEY_MAP[columnKey] ?? columnKey
